import type { PendingEpisodeReview } from "@/components/dataset/annotation";
import type { DatasetDetailTab } from "./datasetDetailTabs";

/**
 * 상세 창·상세 페이지 이탈 가드의 판단. 순수 로직만 둔다(셸은 연결만 한다).
 *
 * 가드는 저장하지 않은 편집이 있을 때 나가려 하면 확인을 요청한다. 그 외 동작은 없다.
 * **가드는 저장하지 않는다.** 무엇을 저장하는지는 그 편집 표면의 저장 버튼이 표시한다. 가드가
 * 대신 저장하면 사용자가 다시 보지 않은 값이 서버로 간다. 그래서 선택지는 둘뿐이다.
 * - 머무른다. 사용자가 저장 버튼을 직접 누르러 간다.
 * - 편집을 버리고 나간다.
 *
 * 이 가드는 데이터셋 리뷰 draft(컨트롤러 메모리)와 에피소드 검토 draft(리뷰 독)만 다룬다.
 * Annotation draft의 이탈 확인과 폐기는 c6 Studio의 `requestLeave`가 소유한다.
 */

/** 이탈 대상. 이 값에 따라 문구가 달라진다. 나가기를 고르면 이 동작을 그대로 실행한다. */
export type DatasetDetailExitIntent = { kind: "close" } | { kind: "tab"; tab: DatasetDetailTab };

export type DatasetDetailUnsavedEdits = {
  datasetReviewDirty: boolean;
  episodeReviewDirty: boolean;
};

export type DatasetDetailExitGuardTargets = {
  datasetReviewDirty: boolean;
  resetDatasetReview: () => void;
  // 저장하지 않은 에피소드 검토가 있으면 그 명령, 없으면 null.
  episodeReview: PendingEpisodeReview | null;
};

/** 저장하지 않은 편집을 모두 저장값으로 되돌린다. 나가기를 고른 뒤에만 호출한다. */
export function discardGuardedEdits(targets: DatasetDetailExitGuardTargets): void {
  if (targets.datasetReviewDirty) {
    targets.resetDatasetReview();
  }
  targets.episodeReview?.revert();
}

/**
 * 어떤 이탈로 무엇이 사라지는지 설명하는 문구. 데이터셋 리뷰와 에피소드 검토는 다른 편집이다.
 * 그래서 하나로 합치지 않는다. 저장 방법도 함께 안내한다. 가드에는 저장 버튼이 없다. 그래서
 * 어디를 눌러야 하는지 문구가 알려야 한다.
 */
export function unsavedEditsCaption(
  edits: DatasetDetailUnsavedEdits,
  intent: DatasetDetailExitIntent,
): string {
  const names = [
    edits.datasetReviewDirty ? "Dataset 리뷰 변경" : null,
    edits.episodeReviewDirty ? "에피소드 검토" : null,
  ].filter((name) => name !== null);
  const target = names.length > 0 ? `${names.join("과 ")} 내용` : "편집 내용";
  const exit = intent.kind === "close" ? "창을 닫으면" : "다른 탭으로 옮기면";
  const keep =
    intent.kind === "close" ? " 창을 최소화하면 그대로 유지됩니다." : " 머무르면 그대로 남습니다.";
  return `${exit} ${target}이 사라집니다. 저장하려면 머무른 뒤 저장 버튼을 눌러 주세요.${keep}`;
}
